// /src/reports/StocktakeReport.js
import React, { useState, useEffect } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, addDays } from "date-fns";
import { toast } from "react-toastify";

// OneStop week start date
const ONE_STOP_WEEK1_START = new Date("2025-02-23"); // Sunday

function getOneStopWeekInfo(date) {
  const diffDays = Math.floor(
    (date - ONE_STOP_WEEK1_START) / (1000 * 60 * 60 * 24)
  );
  const weekNum = Math.floor(diffDays / 7) + 1;
  const weekStart = addDays(ONE_STOP_WEEK1_START, (weekNum - 1) * 7);
  const weekEnd = addDays(weekStart, 6);
  return { weekNum, weekStart, weekEnd };
}

export default function StocktakeReport({ fromDate, toDate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState("daily");

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, viewType]);

  async function fetchData() {
    setLoading(true);

    const { data, error } = await supabase
      .from("stocktake")
      .select("*")
      .gte("date", fromDate)
      .lte("date", toDate);

    if (error) {
      toast.error("Error fetching stocktake data");
      setLoading(false);
      return;
    }

    const processed = data.map((s) => ({
      date: format(new Date(s.date), "yyyy-MM-dd"),
      department: s.department || "",
      variance: parseFloat(s.variance) || 0
    }));

    setRows(processed);
    setLoading(false);
  }

  function groupByWeek(data) {
    const grouped = {};
    data.forEach((row) => {
      const { weekNum, weekStart, weekEnd } = getOneStopWeekInfo(
        new Date(row.date)
      );
      const key = `Week ${weekNum} (${format(weekStart, "dd MMM")} - ${format(
        weekEnd,
        "dd MMM"
      )})`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  }

  function exportCSV() {
    const headers = ["Date/Week", "Department", "Variance (£)"];
    let csvRows = [headers.join(",")];

    if (viewType === "daily") {
      rows.forEach((r) => {
        csvRows.push([r.date, r.department, r.variance.toFixed(2)].join(","));
      });
    } else {
      const weeklyGroups = groupByWeek(rows);
      Object.keys(weeklyGroups).forEach((week) => {
        weeklyGroups[week].forEach((r) => {
          csvRows.push([week, r.department, r.variance.toFixed(2)].join(","));
        });
      });
    }

    saveAs(
      new Blob([csvRows.join("\n")], { type: "text/csv" }),
      `stocktake_report_${format(new Date(), "yyyyMMdd_HHmm")}.csv`
    );
  }

  if (loading) return <p>Loading Stocktake Report...</p>;

  if (viewType === "daily") {
    const total = rows.reduce((sum, r) => sum + r.variance, 0);

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={(e) => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>
          Export CSV
        </button>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Department</th>
              <th>Variance (£)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td>{r.department}</td>
                <td>{r.variance.toFixed(2)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
              <td colSpan="2">Total</td>
              <td>{total.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  } else {
    const weeklyGroups = groupByWeek(rows);
    const grandTotal = rows.reduce((sum, r) => sum + r.variance, 0);

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={(e) => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>
          Export CSV
        </button>

        {Object.keys(weeklyGroups).map((week) => {
          const weekRows = weeklyGroups[week];
          const weekTotal = weekRows.reduce((sum, r) => sum + r.variance, 0);

          return (
            <div key={week} style={{ marginBottom: "20px" }}>
              <h3>{week}</h3>
              <table>
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Variance (£)</th>
                  </tr>
                </thead>
                <tbody>
                  {weekRows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.department}</td>
                      <td>{r.variance.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
                    <td>Weekly Total</td>
                    <td>{weekTotal.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}

        <h3 style={{ marginTop: "10px" }}>
          Grand Total: £{grandTotal.toFixed(2)}
        </h3>
      </div>
    );
  }
}
